/**
 * ProgressExamples.tsx
 * Comprehensive examples of HeroUI Progress component
 * 
 * Showcases all HeroUI Progress features using our shared components
 */

import React, { useState, useEffect } from 'react';
import { PlaygroundPageLayout } from '../shared';
import { Progress } from '@enfomentis/ui';
import { Card, CardBody, CardHeader } from '@enfomentis/ui';
import { Button } from '@enfomentis/ui';


export const ProgressExamples: React.FC = () => {
  const [dynamicValue, setDynamicValue] = useState(0);
  const [uploadProgress, setUploadProgress] = useState(0);
  
  // Simulate dynamic progress
  useEffect(() => {
    const interval = setInterval(() => {
      setDynamicValue(prev => prev >= 100 ? 0 : prev + 1);
    }, 100);
    return () => clearInterval(interval);
  }, []);

  const simulateUpload = () => {
    setUploadProgress(0);
    const interval = setInterval(() => {
      setUploadProgress(prev => {
        if (prev >= 100) {
          clearInterval(interval);
          return 100;
        }
        return prev + Math.random() * 15;
      });
    }, 200);
  };

  return (
    <PlaygroundPageLayout>
      <div className="space-y-8">
        
        {/* Basic Usage */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Basic Usage</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Default Progress</h4>
                  <Progress value={65} />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">With Label</h4>
                  <Progress value={75} label="Loading..." />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Without Value Label</h4>
                  <Progress value={45} showValueLabel={false} />
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Sizes */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Sizes</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Small</h4>
                  <Progress size="sm" value={40} label="Small progress" />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Medium (Default)</h4>
                  <Progress size="md" value={60} label="Medium progress" />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Large</h4>
                  <Progress size="lg" value={80} label="Large progress" />
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Colors */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Colors</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Default</h4>
                  <Progress color="default" value={35} />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Primary</h4>
                  <Progress color="primary" value={50} />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Secondary</h4>
                  <Progress color="secondary" value={65} />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Success</h4>
                  <Progress color="success" value={80} />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Warning</h4>
                  <Progress color="warning" value={45} />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Danger</h4>
                  <Progress color="danger" value={25} />
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* States */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">States</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            {/* Indeterminate */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Indeterminate</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-text-secondary mb-2">Loading</h4>
                    <Progress isIndeterminate color="primary" label="Processing..." />
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-text-secondary mb-2">Without Label</h4>
                    <Progress isIndeterminate color="secondary" />
                  </div>
                </div>
              </CardBody>
            </Card>

            {/* Striped */}
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Striped</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-6">
                  <div>
                    <h4 className="text-sm font-medium text-text-secondary mb-2">Striped Progress</h4>
                    <Progress isStriped value={70} color="primary" />
                  </div>
                  
                  <div>
                    <h4 className="text-sm font-medium text-text-secondary mb-2">Striped Success</h4>
                    <Progress isStriped value={85} color="success" />
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Dynamic Progress */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Dynamic Progress</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Auto-incrementing Progress</h4>
                  <Progress value={dynamicValue} color="primary" label="Auto progress" />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Upload Simulation</h4>
                  <div className="space-y-3">
                    <Progress 
                      value={uploadProgress} 
                      color="success" 
                      label="Uploading files..."
                      showValueLabel={uploadProgress > 0}
                    />
                    <Button 
                      onPress={simulateUpload}
                      color="primary"
                      size="sm"
                      isDisabled={uploadProgress > 0 && uploadProgress < 100}
                    >
                      {uploadProgress > 0 && uploadProgress < 100 ? 'Uploading...' : 'Start Upload'}
                    </Button>
                  </div>
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Custom Formatting */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Custom Formatting</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Percentage Format</h4>
                  <Progress 
                    value={0.75} 
                    formatOptions={{ style: 'percent' }}
                    color="primary"
                    label="Completion rate"
                  />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Custom Units (MB)</h4>
                  <Progress 
                    value={42} 
                    color="secondary"
                    label="Downloaded: 42 MB / 100 MB"
                    showValueLabel={false}
                  />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Decimal Places</h4>
                  <Progress 
                    value={67.89} 
                    formatOptions={{ maximumFractionDigits: 2 }}
                    color="success"
                    label="Processing accuracy"
                  />
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

        {/* Trading Context Examples */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Trading Context</h3>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            
            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Portfolio Allocation</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-text-secondary">Stocks</span>
                      <span className="text-sm text-text-primary">$45,000</span>
                    </div>
                    <Progress value={60} color="primary" showValueLabel={false} />
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-text-secondary">Bonds</span>
                      <span className="text-sm text-text-primary">$22,500</span>
                    </div>
                    <Progress value={30} color="secondary" showValueLabel={false} />
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-text-secondary">Cash</span>
                      <span className="text-sm text-text-primary">$7,500</span>
                    </div>
                    <Progress value={10} color="warning" showValueLabel={false} />
                  </div>
                </div>
              </CardBody>
            </Card>

            <Card>
              <CardHeader>
                <h4 className="text-lg font-semibold text-text-primary">Risk Metrics</h4>
              </CardHeader>
              <CardBody>
                <div className="space-y-4">
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-text-secondary">Daily Risk Used</span>
                      <span className="text-sm text-text-primary">65%</span>
                    </div>
                    <Progress value={65} color="warning" showValueLabel={false} />
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-text-secondary">Win Rate</span>
                      <span className="text-sm text-success">78%</span>
                    </div>
                    <Progress value={78} color="success" showValueLabel={false} />
                  </div>
                  
                  <div>
                    <div className="flex justify-between items-center mb-2">
                      <span className="text-sm text-text-secondary">Account Drawdown</span>
                      <span className="text-sm text-loss">12%</span>
                    </div>
                    <Progress value={12} color="danger" showValueLabel={false} />
                  </div>
                </div>
              </CardBody>
            </Card>
          </div>
        </div>

        {/* Loading States */}
        <div>
          <h3 className="text-xl font-semibold text-text-primary mb-4">Loading States</h3>
          <Card>
            <CardBody>
              <div className="space-y-6">
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Data Synchronization</h4>
                  <Progress isIndeterminate color="primary" label="Syncing market data..." />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Report Generation</h4>
                  <Progress value={42} color="secondary" label="Generating monthly report..." />
                </div>
                
                <div>
                  <h4 className="text-sm font-medium text-text-secondary mb-2">Portfolio Analysis</h4>
                  <Progress isIndeterminate isStriped color="success" label="Analyzing portfolio..." />
                </div>
              </div>
            </CardBody>
          </Card>
        </div>

      </div>
    </PlaygroundPageLayout>
  );
};